/**
 * Imports a chart-of-accounts ("אינדקס חשבונות") Excel export as account cards.
 * Shared by the RAVGONIT and NIHULIT entries — only the title, the format hint and the
 * preview/commit callbacks differ between vendors.
 */
import React, { useState } from 'react';
import { Button, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import DropzoneArea from '../DropzoneArea';

const normalizeMissingPalCodes = (list) =>
    (list ?? []).map(pc => {
        const name = typeof pc === 'string' ? pc : (pc?.palCodeName || pc?.PalCodeName || '');
        const id = typeof pc === 'string' ? pc : (pc?.palCodeId || pc?.PalCodeId || name);
        return { palCodeName: name, palCodeId: id };
    }).filter(pc => pc.palCodeName);

const AccountIndexImportModal = ({
    open,
    onClose,
    title,
    formatHint,
    previewAccountIndex,
    commitAccountIndex,
    createMissingPalCode,
    direction,
    t,
    palCodeCategoryOptions = [],
    palCodes = [],
    Constants = {},
    user
}) => {
    const customStyles = {
        container: (provided) => ({ ...provided, width: '100%', boxSizing: 'border-box !important' }),
        control: (provided) => ({
            ...provided,
            minHeight: '40px',
            width: '100% !important',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            boxShadow: 'none',
            padding: '0 10px',
            textAlign: 'left',
            boxSizing: 'border-box !important',
            '&:focus-within': { border: '2px solid var(--Foundation-Blue-Normal, #304FFF)' },
        }),
        placeholder: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        singleValue: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        menu: (provided) => ({ ...provided, textAlign: 'left', color: 'black' }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#F9AA2A' : state.isFocused ? '#ebebeb' : null,
            color: 'black',
        }),
        menuPortal: (provided) => ({ ...provided, zIndex: 2002 }),
    };

    const palCodeTypeOptions = (Constants.PalCodeType || []).map(type => ({ label: type, value: type }));
    const palCodeCategorySelectOptions = palCodeCategoryOptions.map(cat => ({
        label: cat.palCodeCategoryId,
        value: cat.palCodeCategoryId
    }));

    const [file, setFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [parsedRows, setParsedRows] = useState(0);
    const [newCards, setNewCards] = useState(0);
    const [renumberedCards, setRenumberedCards] = useState(0);
    const [existingCards, setExistingCards] = useState(0);
    const [accountCards, setAccountCards] = useState([]);
    const [missingPalCodes, setMissingPalCodes] = useState([]);
    const [editingPalCode, setEditingPalCode] = useState(null);
    const [bulkMode, setBulkMode] = useState(false);
    const [selectedPalCodesForBulk, setSelectedPalCodesForBulk] = useState([]);
    const [palCodeForm, setPalCodeForm] = useState({ palCodeId: '', palCodeType: '', palCodeCategory: null });
    const [bulkForm, setBulkForm] = useState({ palCodeType: '', palCodeCategory: null });

    const missingPalCodeNames = missingPalCodes.map(pc => pc.palCodeName);

    const calculateNextOrderNumber = (category) => {
        if (!category || palCodes.length === 0) return 100;
        const maxOrderNumber = palCodes
            .filter(pc => pc.palCodeCategory === category)
            .reduce((max, pc) => Math.max(max, pc.orderNumber || 0), 0);
        return maxOrderNumber > 0 ? maxOrderNumber + 100 : 100;
    };

    const handleDrop = (files) => {
        if (!files?.length) return;
        setFile(files[0]);
        setPreviewDone(false);
        setPreviewError(null);
    };

    const handlePreview = async () => {
        if (!file) return;
        try {
            setProcessing(true);
            setPreviewError(null);
            const res = await previewAccountIndex(file);
            setAccountCards(res?.accountCards ?? []);
            setMissingPalCodes(normalizeMissingPalCodes(res?.missingPalCodes));
            setParsedRows(res?.parsedRows ?? 0);
            setNewCards(res?.newCards ?? 0);
            setRenumberedCards(res?.renumberedCards ?? 0);
            setExistingCards(res?.existingCards ?? 0);
            setPreviewDone(true);
            setEditingPalCode(null);
            setBulkMode(false);
            setSelectedPalCodesForBulk([]);
        } catch (err) {
            console.error('Account index preview failed:', err);
            setPreviewDone(false);
            setPreviewError(err?.message || t('PreviewFailed'));
        } finally {
            setProcessing(false);
        }
    };

    const startEditingPalCode = (pc) => {
        setEditingPalCode(pc.palCodeName);
        setPalCodeForm({
            palCodeId: pc.palCodeId || pc.palCodeName,
            palCodeType: '',
            palCodeCategory: null
        });
    };

    const handleCreatePalCode = async (palCodeName) => {
        if (!palCodeForm.palCodeId || !palCodeForm.palCodeType) return;

        try {
            setProcessing(true);
            const response = await createMissingPalCode({
                palCodeId: palCodeForm.palCodeId,
                palCodeType: palCodeForm.palCodeType,
                palCodeCategory: palCodeForm.palCodeCategory,
                orderNumber: palCodeForm.palCodeCategory
                    ? calculateNextOrderNumber(palCodeForm.palCodeCategory)
                    : 100,
                accountantId: user?.accountantId,
            });
            if (!response.ok) throw new Error(await response.text());

            setMissingPalCodes(prev => prev.filter(pc => pc.palCodeName !== palCodeName));
            setEditingPalCode(null);
            setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('PalCode creation error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleBulkCreatePalCodes = async () => {
        if (!bulkForm.palCodeType || selectedPalCodesForBulk.length === 0) return;

        try {
            setProcessing(true);
            const baseOrderNumber = bulkForm.palCodeCategory
                ? calculateNextOrderNumber(bulkForm.palCodeCategory)
                : 100;

            const selectedItems = missingPalCodes.filter(pc =>
                selectedPalCodesForBulk.includes(pc.palCodeName)
            );

            const responses = await Promise.all(selectedItems.map((pc, index) =>
                createMissingPalCode({
                    palCodeId: pc.palCodeId || pc.palCodeName,
                    palCodeType: bulkForm.palCodeType,
                    palCodeCategory: bulkForm.palCodeCategory,
                    orderNumber: baseOrderNumber + (index * 100),
                    accountantId: user?.accountantId,
                })
            ));

            const failed = responses.filter(r => !r.ok);
            if (failed.length > 0) throw new Error(`${failed.length} PalCodes failed to create`);

            setMissingPalCodes(prev =>
                prev.filter(pc => !selectedPalCodesForBulk.includes(pc.palCodeName))
            );
            setBulkMode(false);
            setSelectedPalCodesForBulk([]);
            setBulkForm({ palCodeType: '', palCodeCategory: null });
        } catch (err) {
            console.error('Bulk PalCode creation error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleCommit = async () => {
        if (!file) return;
        try {
            setProcessing(true);
            const res = await commitAccountIndex(file);
            if (res?.errorMessages?.length > 0) {
                console.error('Import errors:', res.errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Account index commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewDone(false);
        setPreviewError(null);
        setAccountCards([]);
        setMissingPalCodes([]);
        setParsedRows(0);
        setNewCards(0);
        setRenumberedCards(0);
        setExistingCards(0);
        setEditingPalCode(null);
        setBulkMode(false);
        setSelectedPalCodesForBulk([]);
        setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
        setBulkForm({ palCodeType: '', palCodeCategory: null });
        onClose();
    };

    if (!open) return null;

    const alignment = direction === 'rtl' ? 'right' : 'left';

    return (
        <div className="download-modal-overlay" onClick={handleClose} style={{ zIndex: 2000 }}>
            <div className="download-modal" onClick={e => e.stopPropagation()} style={{ zIndex: 2001 }}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1400px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">{title}</h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div>
                        <h5>{t('UploadFile')}</h5>
                        {formatHint && (
                            <p style={{ fontSize: 12, color: '#666', marginBottom: 10, lineHeight: 1.6 }}>
                                {formatHint}
                            </p>
                        )}
                        <DropzoneArea
                            key={file ? file.name : 'account-index-empty'}
                            onDrop={handleDrop}
                            accept=".xls,.xlsx"
                            filesLimit={1}
                        />
                        {file && <div style={{ marginTop: 8 }}>{t('FileSelected')}: {file.name}</div>}
                    </div>

                    {previewError && (
                        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#fdecea', borderRadius: 6, border: '1px solid #f44336', color: '#b71c1c', fontSize: 14, textAlign: alignment }}>
                            {previewError}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button variant="contained" onClick={handlePreview} disabled={!file || processing}>
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing || missingPalCodes.length > 0 || newCards === 0}
                            onClick={handleCommit}
                        >
                            {processing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {previewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: 14, color: '#666' }}>
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{parsedRows}</strong>
                                    {' | '}{t('NewAccountCards') || 'כרטיסים חדשים'}: <strong>{newCards}</strong>
                                    {' | '}{t('RenumberedAccountCards') || 'במזהה חדש'}: <strong>{renumberedCards}</strong>
                                    {' | '}{t('ExistingAccountCards') || 'כרטיסים קיימים'}: <strong>{existingCards}</strong>
                                </p>
                                {renumberedCards > 0 && (
                                    <p style={{ fontSize: 13, color: '#0d47a1' }}>
                                        {t('RenumberedAccountCardsNote')
                                            || 'מזהה שכבר תפוס בתיק על ידי כרטיס אחר יקבל מזהה פנוי, ומספר הכרטיס מהתוכנה החיצונית יישמר בכרטיס.'}
                                    </p>
                                )}
                                {existingCards > 0 && (
                                    <p style={{ fontSize: 13, color: '#856404' }}>
                                        {t('ExistingAccountCardsSkipped') || 'כרטיסים שקיימים בתיק לא ישונו בייבוא.'}
                                    </p>
                                )}
                            </div>

                            {missingPalCodes.length > 0 && (
                                <div style={{ marginBottom: 25, padding: 15, backgroundColor: '#fff3cd', borderRadius: 5, border: '1px solid #ffc107' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
                                        <div>
                                            <h5 style={{ color: '#856404', margin: 0 }}>
                                                {t('MissingPalCodes') || 'חשבונות ראשיים חסרים'}
                                            </h5>
                                            <p style={{ fontSize: 14, color: '#856404', margin: '5px 0 0 0' }}>
                                                {t('MissingPalCodesDescription') || 'יש ליצור את החשבונות הראשיים הבאים לפני הייבוא:'}
                                            </p>
                                        </div>
                                        <Button
                                            variant={bulkMode ? 'contained' : 'outlined'}
                                            size="small"
                                            onClick={() => {
                                                setBulkMode(!bulkMode);
                                                setSelectedPalCodesForBulk([]);
                                                setEditingPalCode(null);
                                                if (!bulkMode) {
                                                    setBulkForm({ palCodeType: '', palCodeCategory: null });
                                                }
                                            }}
                                        >
                                            {bulkMode ? (t('CancelBulk') || 'ביטול יצירה מרובה') : (t('BulkCreate') || 'יצירה מרובה')}
                                        </Button>
                                    </div>

                                    {bulkMode && (
                                        <div style={{ marginBottom: 20, padding: 15, backgroundColor: '#f0f0f0', borderRadius: 5 }}>
                                            <h6 style={{ marginBottom: 10 }}>{t('BulkCreatePalCodes') || 'יצירת חשבונות ראשיים מרובים'}</h6>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                                                        {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                    </label>
                                                    <Select
                                                        placeholder={t('Select') || 'בחר'}
                                                        options={palCodeTypeOptions}
                                                        value={palCodeTypeOptions.find(opt => opt.value === bulkForm.palCodeType) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeType: selectedOption?.value || '' });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                </div>
                                                <div style={{ marginBottom: 8 }}>
                                                    <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                                                        {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                    </label>
                                                    <Select
                                                        placeholder={t('None') || 'ללא'}
                                                        options={palCodeCategorySelectOptions}
                                                        value={palCodeCategorySelectOptions.find(opt => opt.value === bulkForm.palCodeCategory) || null}
                                                        onChange={(selectedOption) => {
                                                            setBulkForm({ ...bulkForm, palCodeCategory: selectedOption?.value || null });
                                                        }}
                                                        styles={customStyles}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        isClearable
                                                    />
                                                </div>
                                                <div style={{ marginTop: 10 }}>
                                                    <p style={{ fontSize: 12, color: '#666', marginBottom: 5 }}>
                                                        {t('SelectedPalCodes') || 'נבחרו'}: {selectedPalCodesForBulk.length} / {missingPalCodes.length}
                                                    </p>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        size="small"
                                                        onClick={handleBulkCreatePalCodes}
                                                        disabled={processing || !bulkForm.palCodeType || selectedPalCodesForBulk.length === 0}
                                                        fullWidth
                                                    >
                                                        {t('CreateSelected') || `צור ${selectedPalCodesForBulk.length} חשבונות`}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {missingPalCodes.map((pc, idx) => (
                                        <div key={`${pc.palCodeName}-${idx}`} style={{ marginBottom: 15, padding: 10, backgroundColor: '#fff', borderRadius: 3 }}>
                                            {editingPalCode === pc.palCodeName ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                    <TextField
                                                        label={t('PalCodeId') || 'מזהה חשבון ראשי'}
                                                        value={palCodeForm.palCodeId}
                                                        onChange={(e) => setPalCodeForm({ ...palCodeForm, palCodeId: e.target.value })}
                                                        size="small"
                                                        fullWidth
                                                    />
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                                                            {t('PalCodeType') || 'סוג חשבון ראשי'} <span style={{ color: 'red' }}>*</span>
                                                        </label>
                                                        <Select
                                                            placeholder={t('Select') || 'בחר'}
                                                            options={palCodeTypeOptions}
                                                            value={palCodeTypeOptions.find(opt => opt.value === palCodeForm.palCodeType) || null}
                                                            onChange={(selectedOption) => {
                                                                setPalCodeForm({ ...palCodeForm, palCodeType: selectedOption?.value || '' });
                                                            }}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 8 }}>
                                                        <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                                                            {t('PalCodeCategory') || 'קטגוריית חשבון ראשי'}
                                                        </label>
                                                        <Select
                                                            placeholder={t('None') || 'ללא'}
                                                            options={palCodeCategorySelectOptions}
                                                            value={palCodeCategorySelectOptions.find(opt => opt.value === palCodeForm.palCodeCategory) || null}
                                                            onChange={(selectedOption) => {
                                                                setPalCodeForm({ ...palCodeForm, palCodeCategory: selectedOption?.value || null });
                                                            }}
                                                            styles={customStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                            isClearable
                                                        />
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 10 }}>
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => handleCreatePalCode(pc.palCodeName)}
                                                            disabled={processing || !palCodeForm.palCodeId || !palCodeForm.palCodeType}
                                                        >
                                                            {t('create') || 'צור'}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => {
                                                                setEditingPalCode(null);
                                                                setPalCodeForm({ palCodeId: '', palCodeType: '', palCodeCategory: null });
                                                            }}
                                                        >
                                                            {t('Cancel') || 'ביטול'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                        {bulkMode && (
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedPalCodesForBulk.includes(pc.palCodeName)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedPalCodesForBulk(prev => [...prev, pc.palCodeName]);
                                                                    } else {
                                                                        setSelectedPalCodesForBulk(prev => prev.filter(name => name !== pc.palCodeName));
                                                                    }
                                                                }}
                                                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                            />
                                                        )}
                                                        <span style={{ fontWeight: 'bold' }}>{pc.palCodeName}</span>
                                                    </div>
                                                    {!bulkMode && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => startEditingPalCode(pc)}
                                                        >
                                                            {t('CreatePalCode') || 'צור חשבון ראשי'}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {accountCards.length === 0 ? (
                                <p style={{ color: '#666' }}>{t('NoAccountCardsFound') || 'לא נמצאו כרטיסי חשבון'}</p>
                            ) : (
                                <>
                                    <h5>{t('AccountCards') || 'כרטיסי חשבון'}</h5>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ padding: 10, textAlign: alignment, border: '1px solid #ddd' }}>{t('CardId') || 'מזהה כרטיס'}</th>
                                                <th style={{ padding: 10, textAlign: alignment, border: '1px solid #ddd' }}>{t('CardName') || 'שם הכרטיס'}</th>
                                                <th style={{ padding: 10, textAlign: alignment, border: '1px solid #ddd' }}>{t('PalCode') || 'חשבון ראשי'}</th>
                                                <th style={{ padding: 10, textAlign: alignment, border: '1px solid #ddd' }}>{t('AccountCaseId') || 'מס.ע.מ'}</th>
                                                <th style={{ padding: 10, textAlign: alignment, border: '1px solid #ddd' }}>{t('Status') || 'סטטוס'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {accountCards.map((entry, i) => {
                                                const isMissingPalCode = entry.palCode && missingPalCodeNames.includes(entry.palCode);
                                                return (
                                                    <tr
                                                        key={`${entry.cardId}-${i}`}
                                                        style={{
                                                            backgroundColor: isMissingPalCode
                                                                ? '#fff3cd'
                                                                : entry.alreadyExists
                                                                    ? '#eeeeee'
                                                                    : entry.importedAsCardId
                                                                        ? '#e3f2fd'
                                                                        : (i % 2 === 0 ? '#fff' : '#f9f9f9'),
                                                        }}
                                                    >
                                                        <td style={{ padding: 10, border: '1px solid #ddd', textAlign: alignment }}>
                                                            {entry.importedAsCardId ? (
                                                                <>
                                                                    <span style={{ fontWeight: 600 }}>{entry.importedAsCardId}</span>
                                                                    <span style={{ marginInlineStart: 6, color: '#666', textDecoration: 'line-through' }}>
                                                                        {entry.cardId}
                                                                    </span>
                                                                </>
                                                            ) : (entry.cardId || '')}
                                                        </td>
                                                        <td style={{ padding: 10, border: '1px solid #ddd', textAlign: alignment }}>{entry.cardName || ''}</td>
                                                        <td style={{ padding: 10, border: '1px solid #ddd', textAlign: alignment, color: isMissingPalCode ? '#856404' : 'inherit' }}>
                                                            {entry.palCode || ''}
                                                            {isMissingPalCode && <span style={{ color: '#dc3545', marginInlineStart: 5 }}>*</span>}
                                                        </td>
                                                        <td style={{ padding: 10, border: '1px solid #ddd', textAlign: alignment }}>{entry.accountCaseId || ''}</td>
                                                        <td style={{ padding: 10, border: '1px solid #ddd', textAlign: alignment, color: '#666' }}>
                                                            {entry.alreadyExists
                                                                ? (t('AlreadyExists') || 'קיים - לא ייובא')
                                                                : entry.importedAsCardId
                                                                    ? (t('ImportedWithNewCardId') || 'מזהה תפוס - ייובא במזהה חדש')
                                                                    : (t('New') || 'חדש')}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AccountIndexImportModal;
